<div class="detail-page">
  <a [routerLink]="backLink" class="back-link" title="Back to export requests" aria-label="Back to export requests">
    <span class="material-icons">arrow_back</span>
  </a>

  @if (isLoading()) {
    <p class="hint">Loading...</p>
  } @else if (notFound()) {
    <p class="hint">This export request doesn't exist, or you don't have access to it.</p>
  } @else {
    <app-error-banner [message]="errorMessage()" />

    @if (request(); as r) {
      <div class="detail-header">
        <app-status-badge [status]="r.status" />
        <h1><code>{{ r.epc }}</code></h1>
        <span class="meta parties">
          {{ companyLabel(r.exporterCompanyId) }}
          <span class="material-icons">arrow_forward</span>
          {{ companyLabel(r.importerCompanyId) }}
        </span>
        <span class="meta">Raised {{ r.createdAt | date: 'medium' }}</span>
        @if (r.decidedAt) {
          <span class="meta">Decided {{ r.decidedAt | date: 'medium' }}</span>
        }
        @if (r.declineReason) {
          <p class="decline-reason">Decline reason: {{ r.declineReason }}</p>
        }
        @if (r.traceFailureReason) {
          <p class="decline-reason">Trace failure: {{ r.traceFailureReason }}</p>
        }
      </div>

      @if (canDecide) {
        <section class="decision-card">
          @if (isLoadingPreview()) {
            <p class="hint">Checking whether accepting will succeed...</p>
          } @else if (previewError()) {
            <p class="preview-note preview-note--warn">{{ previewError() }}</p>
          } @else if (chainPreview(); as preview) {
            @if (preview.wouldSucceed) {
              <p class="preview-note preview-note--ok">
                <span class="material-icons">check_circle</span>
                Accepting will succeed.
              </p>
            } @else {
              <p class="preview-note preview-note--warn">
                <span class="material-icons">warning_amber</span>
                Accepting is likely to fail: {{ preview.problem }}
                @if (preview.problemCode) {
                  <code>({{ preview.problemCode }})</code>
                }
              </p>
            }
          }

          <div class="decision-actions">
            <button class="primary-btn" (click)="accept()" [disabled]="isAccepting()">
              <span class="material-icons">check</span>
              {{ isAccepting() ? 'Accepting...' : 'Accept' }}
            </button>
            <button class="secondary-btn" (click)="openDeclineDialog()" [disabled]="isAccepting()">
              <span class="material-icons">close</span>
              Decline
            </button>
          </div>
        </section>
      }

      <section class="chain-section">
        <h2>Chain submissions</h2>

        <app-error-banner [message]="chainSubmissionsError()" />

        @if (isLoadingChainSubmissions()) {
          <p class="hint">Loading...</p>
        } @else if (chainSubmissions().length === 0) {
          <p class="hint">No chain submissions yet.</p>
        } @else {
          <table>
            <thead>
              <tr>
                <th>Side</th>
                <th>Network</th>
                <th>Status</th>
                <th>Tx hash</th>
                <th>Ledger seq</th>
                <th>Error</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              @for (submission of chainSubmissions(); track submission.id) {
                <tr>
                  <td>{{ submission.direction }}</td>
                  <td>{{ submission.network }}</td>
                  <td><app-status-badge [status]="submission.status" /></td>
                  <td><code>{{ submission.txHash ?? '—' }}</code></td>
                  <td>{{ submission.ledgerSeq ?? '—' }}</td>
                  <td>{{ submission.errorDesc ?? '—' }}</td>
                  <td>
                    @if (canRetry(submission)) {
                      <button
                        class="secondary-btn"
                        (click)="retry(submission)"
                        [disabled]="retryingSubmissionId() === submission.id"
                      >
                        {{ retryingSubmissionId() === submission.id ? 'Retrying...' : 'Retry' }}
                      </button>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </section>

      <section class="chain-section">
        <h2>Destination notifications</h2>

        <app-error-banner [message]="notificationsError()" />

        @if (isLoadingNotifications()) {
          <p class="hint">Loading...</p>
        } @else if (notifications().length === 0) {
          <p class="hint">
            None yet — one appears per confirmed importer-side chain submission, once the destination middleware has
            been told.
          </p>
        } @else {
          <table>
            <thead>
              <tr>
                <th>Country</th>
                <th>Status</th>
                <th>Attempts</th>
                <th>Last error</th>
                <th>Sent</th>
              </tr>
            </thead>
            <tbody>
              @for (n of notifications(); track n.id) {
                <tr>
                  <td>{{ n.countryCode }}</td>
                  <td><app-status-badge [status]="n.status" /></td>
                  <td>{{ n.attempts }}</td>
                  <td>{{ n.lastError ?? '—' }}</td>
                  <td>{{ n.sentAt ? (n.sentAt | date: 'medium') : '—' }}</td>
                </tr>
              }
            </tbody>
          </table>
        }
      </section>

      <section class="events-section">
        <h2>Selected events ({{ r.eventCount }})</h2>

        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Event type</th>
              <th>Biz step</th>
              <th>Location</th>
              <th>Time</th>
            </tr>
          </thead>
          <tbody>
            @for (event of r.events; track event.eventId) {
              <tr>
                <td>{{ event.ordinal }}</td>
                <td>{{ event.eventType }}</td>
                <td>{{ event.bizStep }}</td>
                <td>{{ event.bizLocation }}</td>
                <td>{{ event.eventTime | date: 'medium' }}</td>
              </tr>
            }
          </tbody>
        </table>
      </section>

      <section class="xml-preview-section">
        <h2>Export XML</h2>
        <p class="meta xml-preview-intro">
          Render this selection through a country's own submission template — an export artifact for an operator or
          customs system, separate from what reaches the blockchain.
        </p>

        <app-error-banner [message]="xmlPreviewError()" />
        <app-error-banner [message]="downloadError()" />

        <div class="xml-preview-controls">
          <app-country-select
            label="Country"
            placeholder="Defaults to your own company's country"
            [(ngModel)]="xmlCountryCode"
            name="xmlCountryCode"
          />
          <button class="secondary-btn" (click)="loadXmlPreview()" [disabled]="isLoadingXmlPreview()">
            <span class="material-icons">description</span>
            {{ isLoadingXmlPreview() ? 'Rendering...' : 'Render XML' }}
          </button>
          <button class="secondary-btn" (click)="downloadXml()" [disabled]="isDownloadingXml()">
            <span class="material-icons">download</span>
            {{ isDownloadingXml() ? 'Downloading...' : 'Download XML' }}
          </button>
          <button class="secondary-btn" (click)="downloadJson()" [disabled]="isDownloadingJson()">
            <span class="material-icons">download</span>
            {{ isDownloadingJson() ? 'Downloading...' : 'Download JSON' }}
          </button>
        </div>

        @if (xmlPreview(); as xp) {
          @if (!xp.wouldSucceed) {
            <p class="preview-note preview-note--warn">
              <span class="material-icons">warning_amber</span>
              {{ xp.problem }}
              @if (xp.problemCode) {
                <code>({{ xp.problemCode }})</code>
              }
            </p>
          } @else {
            @if (xp.withheldTerms.length > 0) {
              <p class="preview-excluded">Withheld by {{ xp.countryCode }}'s rules: {{ xp.withheldTerms.join(', ') }}</p>
            }
            @if (xp.unresolvedTerms.length > 0) {
              <p class="preview-excluded">Not present on these events: {{ xp.unresolvedTerms.join(', ') }}</p>
            }

            <div class="xml-output-header">
              <span class="meta">{{ xp.countryCode }} · {{ xp.eventCount }} event(s)</span>
              <button class="btn-icon" title="Copy" (click)="copyXml()">
                <span class="material-icons">content_copy</span>
              </button>
            </div>
            <pre class="xml-output">{{ xp.xml }}</pre>
          }
        }
      </section>

      @if (canDecide && chainPreview(); as preview) {
        @if (preview.wouldSucceed && previewRecordEntries.length > 0) {
          <section class="preview-section">
            <h2>What your chain will receive</h2>

            @if (preview.excludedFields.length > 0) {
              <p class="preview-excluded">
                Excluded by your country's rules: {{ preview.excludedFields.join(', ') }}
              </p>
            }

            <table class="preview-table">
              <tbody>
                @for (entry of previewRecordEntries; track entry[0]) {
                  <tr>
                    <td class="preview-table__key">{{ entry[0] }}</td>
                    <td><pre class="preview-table__value">{{ entry[1] }}</pre></td>
                  </tr>
                }
              </tbody>
            </table>
          </section>
        }
      }
    }
  }
</div>
